import { useState, useEffect, useCallback, useRef } from 'react';
import type { ModuleState, TruthFlags } from '../api/types';
import { EMPTY_TRUTH } from '../api/types';
import { isDataStale } from '../api/client';

const DEFAULT_REFRESH_MS = 120_000;
const MAX_BACKOFF_MS = 600_000;

interface UseModuleOptions<T> {
  fetcher: (signal: AbortSignal) => Promise<T>;
  extractFreshness?: (data: T) => { last_success: string | null; last_run_status: string | null } | null;
  extractTruth?: (data: T) => Partial<TruthFlags>;
  refreshInterval?: number;
  enabled?: boolean;
}

export function useModule<T>(opts: UseModuleOptions<T>): ModuleState<T> & {
  refetch: () => void;
  crashCount: number;
} {
  // Store opts in ref so doFetch doesn't depend on them (prevents infinite loop
  // when callers pass inline arrow functions)
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const refreshInterval = opts.refreshInterval ?? DEFAULT_REFRESH_MS;
  const enabled = opts.enabled ?? true;

  const [state, setState] = useState<ModuleState<T>>({
    data: null, status: 'idle', error: null,
    fetchedAt: null, dataFreshness: null, isStale: false, staleReason: null, truth: EMPTY_TRUTH,
  });

  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);
  const visibleRef = useRef(!document.hidden);
  const consecutiveFailsRef = useRef(0);
  const crashCountRef = useRef(0);
  const [crashCount, setCrashCount] = useState(0);

  // doFetch has ZERO external deps — reads everything from refs
  const doFetch = useCallback(async () => {
    if (!visibleRef.current) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setState(prev => ({ ...prev, status: prev.data ? prev.status : 'loading', error: null }));

    try {
      const data = await optsRef.current.fetcher(controller.signal);
      if (!mountedRef.current) return;

      const freshness = optsRef.current.extractFreshness?.(data) ?? null;
      const stale = freshness ? isDataStale(freshness.last_success) : false;
      const truth: TruthFlags = { ...EMPTY_TRUTH, ...optsRef.current.extractTruth?.(data) };

      consecutiveFailsRef.current = 0;

      setState({
        data, status: stale ? 'stale' : 'success', error: null,
        fetchedAt: Date.now(), dataFreshness: freshness, isStale: stale,
        staleReason: stale ? 'data_old' : null, truth,
      });
    } catch (err) {
      if (!mountedRef.current) return;
      if (err instanceof DOMException && err.name === 'AbortError') return;

      consecutiveFailsRef.current++;
      crashCountRef.current++;
      setCrashCount(crashCountRef.current);

      setState(prev => ({
        ...prev, status: prev.data ? 'stale' : 'error',
        error: err instanceof Error ? err.message : 'Unknown error',
        staleReason: prev.data ? 'fetch_failing' : null,
      }));
    }
  }, []); // empty deps — reads from optsRef

  // Mount
  useEffect(() => {
    mountedRef.current = true;
    if (enabled) doFetch();
    return () => { mountedRef.current = false; abortRef.current?.abort(); };
  }, [doFetch, enabled]);

  // Auto-refresh with exponential backoff
  useEffect(() => {
    if (!enabled || refreshInterval <= 0) return;

    let timeoutId: ReturnType<typeof setTimeout>;

    const scheduleNext = () => {
      const backoff = consecutiveFailsRef.current > 0
        ? Math.min(refreshInterval * Math.pow(2, consecutiveFailsRef.current), MAX_BACKOFF_MS)
        : refreshInterval;

      timeoutId = setTimeout(() => {
        if (visibleRef.current) {
          doFetch().then(scheduleNext);
        } else {
          scheduleNext();
        }
      }, backoff);
    };

    scheduleNext();

    const onVis = () => {
      visibleRef.current = !document.hidden;
      if (visibleRef.current) doFetch();
    };
    document.addEventListener('visibilitychange', onVis);

    return () => { clearTimeout(timeoutId); document.removeEventListener('visibilitychange', onVis); };
  }, [doFetch, refreshInterval, enabled]);

  return { ...state, refetch: doFetch, crashCount };
}
